import { geoOrthographic, geoMercator, type GeoProjection } from 'd3-geo'

/**
 * SP6 projection helpers.
 *
 * `orthographic` builds the globe-view projection used by `<Globe>` (PR-2).
 * `fitMercatorToBbox` builds the region/country-view projection used by
 * `<RegionMap>` and `<CountryMap>`, fitted to the supplied [minLon, minLat,
 * maxLon, maxLat] bbox with configurable pixel padding.
 * `projectLatLon` is a defensive wrapper around a `GeoProjection` call that
 * returns `null` when the point is invisible (e.g. backside of the globe) or
 * NaN, so marker renderers can skip rather than emit bogus coordinates.
 */
export function orthographic(opts: {
  width: number
  height: number
  rotation: [number, number] | [number, number, number]
}): GeoProjection {
  return geoOrthographic()
    .scale(Math.min(opts.width, opts.height) / 2.1)
    .translate([opts.width / 2, opts.height / 2])
    .rotate(opts.rotation)
    .clipAngle(90)
}

export function fitMercatorToBbox(
  bbox: [number, number, number, number],
  opts: { width: number; height: number; padding?: number },
): GeoProjection {
  const padding = opts.padding ?? 16
  const [minLon, minLat, maxLon, maxLat] = bbox
  const feature = {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'Polygon' as const,
      coordinates: [
        [
          [minLon, minLat],
          [maxLon, minLat],
          [maxLon, maxLat],
          [minLon, maxLat],
          [minLon, minLat],
        ],
      ],
    },
  }
  return geoMercator().fitExtent(
    [
      [padding, padding],
      [opts.width - padding, opts.height - padding],
    ],
    feature,
  )
}

export function projectLatLon(
  proj: GeoProjection,
  lat: number,
  lon: number,
): [number, number] | null {
  // Orthographic projections silently wrap antipodal points to the tangent
  // centre rather than returning null (d3-geo's clipAngle only engages during
  // geoPath streaming). Do an explicit great-circle angular-distance check
  // against the projection's rotation centre when a clipAngle is set, so
  // callers can trust a null return for backside / invisible points.
  const clipAngle = proj.clipAngle?.()
  if (typeof clipAngle === 'number' && clipAngle > 0 && clipAngle < 180) {
    const rot = proj.rotate?.() as [number, number, number?] | undefined
    if (rot) {
      const [rotLon, rotLat] = rot
      const centreLon = -rotLon
      const centreLat = -rotLat
      const toRad = Math.PI / 180
      const phi1 = centreLat * toRad
      const phi2 = lat * toRad
      const dLon = (lon - centreLon) * toRad
      const cosC =
        Math.sin(phi1) * Math.sin(phi2) + Math.cos(phi1) * Math.cos(phi2) * Math.cos(dLon)
      const angularDistanceDeg = Math.acos(Math.min(1, Math.max(-1, cosC))) / toRad
      if (angularDistanceDeg >= clipAngle) return null
    }
  }
  const result = proj([lon, lat])
  if (!result || Number.isNaN(result[0]) || Number.isNaN(result[1])) return null
  return [result[0], result[1]]
}
